import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import DraftsIcon from '@mui/icons-material/Drafts';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import SaveIcon from '@mui/icons-material/Save';
import ContactsIcon from '@mui/icons-material/Contacts';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BlockIcon from '@mui/icons-material/Block';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import InvoiceWorkflowTracker from '../components/InvoiceWorkflowTracker';

interface LinkedVendorInvoice {
  id: number;
  pdf_filename: string | null;
  vendor_name: string | null;
  statement_number: string | null;
  invoice_date: string | null;
  line_items_total: number | null;
  validation_status: string | null;
  created_at: string;
}

interface S3Attachment {
  id: number;
  filename: string;
  content_type: string;
  byte_size: number;
  url: string;
}

interface VendorContactSearchOption {
  id: number;
  full_name: string;
  email: string;
  vendor_id: string;
  vendor_name: string | null;
  location: string;
  contact_type: string;
}

interface VendorSearchOption {
  vendor_id: string;
  vendor_name: string;
  location: string;
}

interface VendorMatch {
  match_method: string | null;
  matched_contact: {
    id: number;
    full_name: string;
    email: string;
    u_vnddispl: string | null;
    vendor_id: string;
    location: string;
    contact_type: string;
    vendor_name: string | null;
  } | null;
  vendor_contact_detail: {
    id: number;
    vendor_name: string;
    vendor_id: string;
    location: string;
    has_invoice_number_region: boolean;
    has_invoice_amount_region: boolean;
    statement_attachment_pattern: string | null;
    use_largest_pdf: boolean;
    servman_vendor_name: string | null;
  } | null;
}

interface OutlookEmailDetail {
  id: number;
  message_id: string;
  email_account: string;
  folder: string;
  subject: string;
  body_preview: string;
  received_at: string;
  has_attachments: boolean;
  is_read: boolean;
  from_name: string;
  from_address: string;
  ignored_at: string | null;
  email_details?: {
    body?: {
      content: string;
      contentType: string;
    };
    to?: Array<{
      emailAddress: {
        name: string;
        address: string;
      };
    }>;
    cc?: Array<{
      emailAddress: {
        name: string;
        address: string;
      };
    }>;
    sentDateTime?: string;
  };
  attachments?: Array<{
    id: string;
    name: string;
    contentType: string;
    size: number;
  }>;
  s3_attachments?: S3Attachment[];
  vendor_invoices?: LinkedVendorInvoice[];
  vendor_match?: VendorMatch;
}

const OutlookEmailDetailPage: React.FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);

  const [email, setEmail] = useState<OutlookEmailDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [reimporting, setReimporting] = useState<boolean>(false);
  const [reparsing, setReparsing] = useState<boolean>(false);
  const [resyncing, setResyncing] = useState<boolean>(false);
  const [diagLog, setDiagLog] = useState<string | null>(null);
  const [diagCopied, setDiagCopied] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);
  const [showAllAttachments, setShowAllAttachments] = useState(false);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<number | null>(null);
  const [patternValue, setPatternValue] = useState('');
  const [patternSaving, setPatternSaving] = useState(false);
  const [useLargestPdf, setUseLargestPdf] = useState(false);
  const [contactSearchOptions, setContactSearchOptions] = useState<VendorContactSearchOption[]>([]);
  const [contactSearchLoading, setContactSearchLoading] = useState(false);
  const [contactSearchInput, setContactSearchInput] = useState('');
  const [savingContact, setSavingContact] = useState(false);
  const [vendorSearchOptions, setVendorSearchOptions] = useState<VendorSearchOption[]>([]);
  const [vendorSearchLoading, setVendorSearchLoading] = useState(false);
  const [vendorSearchInput, setVendorSearchInput] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);
  const [overrideSuccess, setOverrideSuccess] = useState(false);
  const [ignoring, setIgnoring] = useState(false);

  const fetchEmailDetail = async () => {
    setLoading(true);
    try {
      const response: any = await axios.get(`/vendors/outlook_emails/${id}`);
      setEmail(response.data);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to fetch email details';
      showErrorSnackBar(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReimportInvoice = async () => {
    setReimporting(true);
    try {
      await axios.post(`/vendors/outlook_emails/${id}/reimport_invoice`);
      showSuccessSnackBar('Invoice reimport queued — refresh in a moment to see the result');
      await fetchEmailDetail();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Failed to queue reimport';
      showErrorSnackBar(errorMessage);
    } finally {
      setReimporting(false);
    }
  };

  const handleReparseInvoice = async () => {
    setReparsing(true);
    try {
      await axios.post(`/vendors/outlook_emails/${id}/reparse_invoice`);
      showSuccessSnackBar('Invoice re-extraction queued — refresh in a moment to see line items');
      await fetchEmailDetail();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Failed to queue re-extraction';
      showErrorSnackBar(errorMessage);
    } finally {
      setReparsing(false);
    }
  };

  const fetchDiagnosticsLog = async () => {
    setDiagLoading(true);
    setDiagLog(null);
    // Poll for the diagnostics log — the job runs async so the log may not be ready yet
    const maxAttempts = 20;
    const delayMs = 3000;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      try {
        const response: any = await axios.get(`/vendors/outlook_emails/${id}/resync_diagnostics`);
        if (response.data.found && response.data.content?.includes('Resync Complete')) {
          setDiagLog(response.data.content);
          setDiagLoading(false);
          return;
        }
      } catch {
        // keep polling
      }
    }
    setDiagLoading(false);
    showErrorSnackBar('Diagnostics log not ready yet — try the Copy Diagnostics Log button again in a moment');
  };

  const handleCopyDiagLog = async () => {
    if (!diagLog) return;
    await navigator.clipboard.writeText(diagLog);
    setDiagCopied(true);
    setTimeout(() => setDiagCopied(false), 2000);
  };

  const handleResyncEmail = async () => {
    setResyncing(true);
    setDiagLog(null);
    try {
      await axios.post(`/vendors/outlook_emails/${id}/resync_email`);
      showSuccessSnackBar('Full email resync queued — diagnostics log will be available shortly');
      fetchDiagnosticsLog(); // start polling in background (don't await)
      await fetchEmailDetail();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Failed to queue resync';
      showErrorSnackBar(errorMessage);
    } finally {
      setResyncing(false);
    }
  };

  const handleMarkAsRead = async () => {
    if (!email) return;

    try {
      await axios.put(`/vendors/outlook_emails/${id}`, {
        outlook_email: {
          is_read: true,
        },
      });
      showSuccessSnackBar('Email marked as read');
      setEmail({ ...email, is_read: true });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to update email';
      showErrorSnackBar(errorMessage);
    }
  };

  const handleIgnore = async () => {
    setIgnoring(true);
    try {
      await axios.post(`/vendors/outlook_emails/${id}/ignore`);
      showSuccessSnackBar('Email and associated statements marked as ignored');
      await fetchEmailDetail();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Failed to mark as ignored';
      showErrorSnackBar(errorMessage);
    } finally {
      setIgnoring(false);
    }
  };

  const searchContacts = useCallback(async (search: string) => {
    if (search.length < 2) {
      setContactSearchOptions([]);
      return;
    }
    setContactSearchLoading(true);
    try {
      const response: any = await axios.get('/vendors/outlook_emails/vendor_contacts_search', {
        params: { search },
      });
      setContactSearchOptions(response.data);
    } catch {
      setContactSearchOptions([]);
    } finally {
      setContactSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (contactSearchInput.length >= 2) {
        searchContacts(contactSearchInput);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [contactSearchInput, searchContacts]);

  const handleSaveVendorContact = async (contact: VendorContactSearchOption) => {
    setSavingContact(true);
    try {
      await axios.put(`/vendors/outlook_emails/${id}`, {
        outlook_email: { servman_vendor_contact_id: contact.id },
      });
      showSuccessSnackBar(`Vendor contact saved: ${contact.vendor_name || contact.full_name}`);
      await fetchEmailDetail();
    } catch (error: any) {
      showErrorSnackBar(error?.response?.data?.errors?.[0] || 'Failed to save vendor contact');
    } finally {
      setSavingContact(false);
    }
  };

  const searchVendors = useCallback(async (search: string) => {
    if (search.length < 2) {
      setVendorSearchOptions([]);
      return;
    }
    setVendorSearchLoading(true);
    try {
      const location = email?.email_account === 'aptampa@tudi.com' ? 'tampa' : 'pittsburgh';
      const response: any = await axios.get('/vendors/outlook_emails/search_vendors', {
        params: { search, location },
      });
      setVendorSearchOptions(response.data);
    } catch {
      setVendorSearchOptions([]);
    } finally {
      setVendorSearchLoading(false);
    }
  }, [email?.email_account]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (vendorSearchInput.length >= 2) {
        searchVendors(vendorSearchInput);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [vendorSearchInput, searchVendors]);

  const handleCreateVendorOverride = async (vendor: VendorSearchOption) => {
    setSavingOverride(true);
    setOverrideSuccess(false);
    try {
      await axios.post(`/vendors/outlook_emails/${id}/create_vendor_override`, {
        vendor_id: vendor.vendor_id,
        vendor_name: vendor.vendor_name,
      });
      setOverrideSuccess(true);
      showSuccessSnackBar(`Vendor override created for ${vendor.vendor_name} — resync queued`);
      await fetchEmailDetail();
    } catch (error: any) {
      showErrorSnackBar(error?.response?.data?.errors?.[0] || error?.response?.data?.error || 'Failed to create vendor override');
    } finally {
      setSavingOverride(false);
    }
  };

  const handleSavePattern = async () => {
    setPatternSaving(true);
    try {
      await axios.put(`/vendors/outlook_emails/${id}/update_statement_pattern`, { pattern: patternValue, use_largest_pdf: useLargestPdf });
      showSuccessSnackBar('Statement attachment pattern saved');
      await fetchEmailDetail();
    } catch (error: any) {
      showErrorSnackBar(error?.response?.data?.error || 'Failed to save pattern');
    } finally {
      setPatternSaving(false);
    }
  };

  useEffect(() => {
    fetchEmailDetail();
  }, [id]);

  useEffect(() => {
    if (email?.vendor_match?.vendor_contact_detail?.statement_attachment_pattern != null) {
      setPatternValue(email.vendor_match.vendor_contact_detail.statement_attachment_pattern);
    }
    if (email?.vendor_match?.vendor_contact_detail?.use_largest_pdf != null) {
      setUseLargestPdf(email.vendor_match.vendor_contact_detail.use_largest_pdf);
    }
  }, [email?.vendor_match?.vendor_contact_detail?.statement_attachment_pattern, email?.vendor_match?.vendor_contact_detail?.use_largest_pdf]);

  // Auto-select the first statement PDF when email data loads
  useEffect(() => {
    if (!email?.s3_attachments?.length) return;
    const vcd = email.vendor_match?.vendor_contact_detail;
    const pattern = vcd?.statement_attachment_pattern;
    const useLargest = vcd?.use_largest_pdf;
    const pdfAttachments = email.s3_attachments.filter((a) => a.content_type === 'application/pdf');

    if (pdfAttachments.length > 0 && selectedAttachmentId === null) {
      if (useLargest) {
        // Pick the largest PDF by byte_size
        const largest = pdfAttachments.reduce((max, a) => (a.byte_size > max.byte_size ? a : max), pdfAttachments[0]);
        setSelectedAttachmentId(largest.id);
      } else {
        const statementPdfs = pattern
          ? pdfAttachments.filter((a) => {
              try {
                return new RegExp(pattern, 'i').test(a.filename);
              } catch {
                return true;
              }
            })
          : pdfAttachments;
        if (statementPdfs.length > 0) {
          setSelectedAttachmentId(statementPdfs[0].id);
        }
      }
    }
  }, [email?.s3_attachments, email?.vendor_match?.vendor_contact_detail?.statement_attachment_pattern, email?.vendor_match?.vendor_contact_detail?.use_largest_pdf]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!email) {
    return (
      <Box>
        <Typography variant="h6">Email not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.goBack()}>
          Back to Emails
        </Button>
      </Box>
    );
  }

  const formatEmailList = (emailList?: Array<{ emailAddress: { name: string; address: string } }>) => {
    if (!emailList || emailList.length === 0) return '-';
    return emailList.map((e) => `${e.emailAddress.name} <${e.emailAddress.address}>`).join(', ');
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <Box style={{ width: '100%' }}>
      <Box mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
          style={{ marginBottom: 16 }}
        >
          Back to Emails
        </Button>

        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h5" component="h5">
              Email Details
            </Typography>
          </Grid>
          <Grid item>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                color={email.ignored_at ? 'inherit' : undefined}
                startIcon={ignoring ? <CircularProgress size={16} color="inherit" /> : <BlockIcon />}
                onClick={handleIgnore}
                disabled={ignoring || !!email.ignored_at}
              >
                {email.ignored_at ? 'Ignored' : ignoring ? 'Ignoring...' : 'Mark as Ignored'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={resyncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                onClick={handleResyncEmail}
                disabled={resyncing}
              >
                {resyncing ? 'Resyncing...' : 'Resync from Outlook'}
              </Button>
              {!email.is_read && (
                <Button variant="contained" startIcon={<DraftsIcon />} onClick={handleMarkAsRead}>
                  Mark as Read
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {email.ignored_at && (
        <Alert severity="info" icon={<BlockIcon />} sx={{ mb: 2 }}>
          This email was ignored on {new Date(email.ignored_at).toLocaleDateString()}. It is hidden from the Email Review list and its statements are hidden from Vendor Statements.
        </Alert>
      )}

      {(diagLoading || diagLog) && (
        <Card sx={{ mb: 2, bgcolor: 'grey.50' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2">Resync Diagnostics Log</Typography>
              <Box display="flex" gap={1}>
                {diagLog && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopyDiagLog}
                    color={diagCopied ? 'success' : 'primary'}
                  >
                    {diagCopied ? 'Copied!' : 'Copy Diagnostics Log'}
                  </Button>
                )}
              </Box>
            </Box>
            {diagLoading && !diagLog && (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Waiting for resync to complete...
                </Typography>
              </Box>
            )}
            {diagLog && (
              <Box
                sx={{
                  maxHeight: 400,
                  overflow: 'auto',
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Typography
                  component="pre"
                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', m: 0 }}
                >
                  {diagLog}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <ContactsIcon color="action" />
            <Typography variant="h6" style={{ flexGrow: 1 }}>
              Vendor Mapping
            </Typography>
          </Box>
          {email.vendor_match?.vendor_contact_detail ? (
            <>
              <Box p={2} border={1} borderColor="grey.300" borderRadius={1}>
                <Typography variant="body1" fontWeight={500}>
                  {email.vendor_match.vendor_contact_detail.servman_vendor_name ||
                    email.vendor_match.vendor_contact_detail.vendor_name}
                </Typography>
                <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                  <Chip label={`Vendor: ${email.vendor_match.vendor_contact_detail.vendor_id}`} size="small" />
                  <Chip label={email.vendor_match.vendor_contact_detail.location} size="small" />
                </Box>
                {email.vendor_match.vendor_contact_detail.has_invoice_number_region &&
                email.vendor_match.vendor_contact_detail.has_invoice_amount_region ? (
                  <Box mt={1.5} display="flex" flexDirection="column" gap={0.5}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CheckCircleIcon fontSize="small" color="success" />
                      <Typography variant="body2">Invoice Number Region</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CheckCircleIcon fontSize="small" color="success" />
                      <Typography variant="body2">Invoice Amount Region</Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box mt={1.5} display="flex" alignItems="center" gap={1.5} p={1.5} borderRadius={1} sx={{ backgroundColor: 'rgba(2, 136, 209, 0.08)' }}>
                    <CircularProgress size={20} color="info" />
                    <Typography variant="body2" color="info.main" fontWeight={500}>
                      Auto-parsing Statement using Vendor Information
                    </Typography>
                  </Box>
                )}
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Change Vendor Mapping
              </Typography>
              <Box display="flex" gap={1} alignItems="flex-start">
                <Autocomplete
                  size="small"
                  style={{ flex: 1 }}
                  options={vendorSearchOptions}
                  loading={vendorSearchLoading}
                  getOptionLabel={(option) => `${option.vendor_name} (${option.vendor_id})`}
                  onInputChange={(_e, value) => setVendorSearchInput(value)}
                  onChange={(_e, value) => {
                    if (value) handleCreateVendorOverride(value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search vendors..."
                      variant="outlined"
                      placeholder="Type vendor name or ID"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={`${option.vendor_id}-${option.location}`}>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {option.vendor_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {option.vendor_id} · {option.location}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  noOptionsText={vendorSearchInput.length < 2 ? 'Type at least 2 characters' : 'No vendors found'}
                  disabled={savingOverride}
                />
                {savingOverride && <CircularProgress size={24} style={{ marginTop: 8 }} />}
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                No vendor mapped for this sender ({email.from_address}). Select a vendor below to create a
                permanent mapping for all future emails from this address.
              </Typography>
              {overrideSuccess && (
                <Alert severity="success" sx={{ mb: 1.5 }}>
                  Vendor override created — resync is running to update the invoice.
                </Alert>
              )}
              <Box mt={1} display="flex" gap={1} alignItems="flex-start">
                <Autocomplete
                  size="small"
                  style={{ flex: 1 }}
                  options={vendorSearchOptions}
                  loading={vendorSearchLoading}
                  getOptionLabel={(option) => `${option.vendor_name} (${option.vendor_id})`}
                  onInputChange={(_e, value) => setVendorSearchInput(value)}
                  onChange={(_e, value) => {
                    if (value) handleCreateVendorOverride(value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search vendors..."
                      variant="outlined"
                      placeholder="Type vendor name or ID"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={`${option.vendor_id}-${option.location}`}>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {option.vendor_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {option.vendor_id} · {option.location}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  noOptionsText={vendorSearchInput.length < 2 ? 'Type at least 2 characters' : 'No vendors found'}
                  disabled={savingOverride}
                />
                {savingOverride && <CircularProgress size={24} style={{ marginTop: 8 }} />}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Typography variant="h6" style={{ flexGrow: 1 }}>
                  {email.subject || '(No Subject)'}
                </Typography>
                {email.is_read ? (
                  <Chip label="Read" size="small" icon={<DraftsIcon fontSize="small" />} />
                ) : (
                  <Chip label="Unread" color="primary" size="small" icon={<EmailIcon fontSize="small" />} />
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">
                  From:
                </Typography>
              </Box>
              <Typography variant="body1">{email.from_name || '-'}</Typography>
              <Typography variant="body2" color="textSecondary">
                {email.from_address || '-'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CalendarTodayIcon fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">
                  Received:
                </Typography>
              </Box>
              <Typography variant="body1">{formatDateTime(email.received_at)}</Typography>
            </Grid>

            {email.email_details?.to && email.email_details.to.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  To:
                </Typography>
                <Typography variant="body1">{formatEmailList(email.email_details.to)}</Typography>
              </Grid>
            )}

            {email.email_details?.cc && email.email_details.cc.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  CC:
                </Typography>
                <Typography variant="body1">{formatEmailList(email.email_details.cc)}</Typography>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <EmailIcon fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">
                  Account:
                </Typography>
              </Box>
              <Typography variant="body1">{email.email_account}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <FolderIcon fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">
                  Folder:
                </Typography>
              </Box>
              <Typography variant="body1">{email.folder}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {email.s3_attachments &&
        email.s3_attachments.length > 0 &&
        (() => {
          const pattern = email.vendor_match?.vendor_contact_detail?.statement_attachment_pattern;
          const vcdUseLargestPdf = email.vendor_match?.vendor_contact_detail?.use_largest_pdf;
          const pdfAttachments = email.s3_attachments.filter((a) => a.content_type === 'application/pdf');
          const statementPdfs = vcdUseLargestPdf
            ? (() => {
                const largest = pdfAttachments.reduce((max, a) => (a.byte_size > max.byte_size ? a : max), pdfAttachments[0]);
                return largest ? [largest] : [];
              })()
            : pattern
              ? pdfAttachments.filter((a) => {
                  try {
                    return new RegExp(pattern, 'i').test(a.filename);
                  } catch {
                    return true;
                  }
                })
              : pdfAttachments;
          const nonPdfAttachments = email.s3_attachments.filter((a) => a.content_type !== 'application/pdf');
          const otherPdfs = pdfAttachments.filter((a) => !statementPdfs.some((s) => s.id === a.id));
          const selectedAttachment = email.s3_attachments.find((a) => a.id === selectedAttachmentId);

          return (
            <Card style={{ marginBottom: 16 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <AttachFileIcon />
                  <Typography variant="h6" style={{ flexGrow: 1 }}>
                    Statement PDF
                  </Typography>
                  {selectedAttachment && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      href={selectedAttachment.url}
                      download={selectedAttachment.filename}
                    >
                      Download
                    </Button>
                  )}
                </Box>

                {/* Attachment selector when multiple PDFs exist */}
                {pdfAttachments.length > 1 && (
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Select attachment to view:
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {statementPdfs.map((a) => (
                        <Chip
                          key={a.id}
                          label={a.filename}
                          size="small"
                          color={selectedAttachmentId === a.id ? 'primary' : 'default'}
                          variant={selectedAttachmentId === a.id ? 'filled' : 'outlined'}
                          onClick={() => setSelectedAttachmentId(a.id)}
                          icon={<AttachFileIcon />}
                        />
                      ))}
                      {showAllAttachments &&
                        otherPdfs.map((a) => (
                          <Chip
                            key={a.id}
                            label={a.filename}
                            size="small"
                            color={selectedAttachmentId === a.id ? 'primary' : 'default'}
                            variant={selectedAttachmentId === a.id ? 'filled' : 'outlined'}
                            onClick={() => setSelectedAttachmentId(a.id)}
                          />
                        ))}
                      {otherPdfs.length > 0 && !showAllAttachments && (
                        <Chip
                          label={`+${otherPdfs.length} more`}
                          size="small"
                          variant="outlined"
                          onClick={() => setShowAllAttachments(true)}
                        />
                      )}
                    </Box>
                  </Box>
                )}

                {/* Embedded PDF viewer */}
                {selectedAttachment && selectedAttachment.content_type === 'application/pdf' ? (
                  <Box
                    sx={{
                      border: 1,
                      borderColor: 'grey.300',
                      borderRadius: 1,
                      overflow: 'hidden',
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        bgcolor: 'grey.50',
                        borderBottom: 1,
                        borderColor: 'grey.300',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {selectedAttachment.filename}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formatFileSize(selectedAttachment.byte_size)}
                      </Typography>
                    </Box>
                    <iframe
                      src={selectedAttachment.url}
                      title={selectedAttachment.filename}
                      style={{
                        width: '100%',
                        height: '800px',
                        border: 'none',
                      }}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No PDF attachments found.
                  </Typography>
                )}

                {/* Statement attachment filter config */}
                {email.vendor_match?.vendor_contact_detail && (
                  <Box p={1.5} bgcolor="grey.50" borderRadius={1} mb={nonPdfAttachments.length > 0 ? 2 : 0}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Statement Attachment Filter
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={useLargestPdf}
                          onChange={(e) => setUseLargestPdf(e.target.checked)}
                        />
                      }
                      label={<Typography variant="body2">Always use largest PDF</Typography>}
                      sx={{ mb: useLargestPdf ? 0 : 1 }}
                    />
                    {!useLargestPdf && (
                      <Box display="flex" gap={1} alignItems="center">
                        <TextField
                          size="small"
                          variant="outlined"
                          value={patternValue}
                          onChange={(e) => setPatternValue(e.target.value)}
                          placeholder="e.g. statement|invoice"
                          label="Filename pattern (regex)"
                          style={{ flex: 1 }}
                        />
                      </Box>
                    )}
                    <Box display="flex" justifyContent="flex-end" mt={1}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={patternSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                        onClick={handleSavePattern}
                        disabled={patternSaving || (patternValue === (pattern || '') && useLargestPdf === (vcdUseLargestPdf || false))}
                      >
                        Save
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Non-PDF attachments */}
                {nonPdfAttachments.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Other Attachments
                    </Typography>
                    <Grid container spacing={1}>
                      {nonPdfAttachments.map((attachment) => {
                        const isImage = attachment.content_type?.startsWith('image/');
                        return (
                          <Grid item xs={12} key={attachment.id}>
                            <Box p={1.5} border={1} borderColor="grey.200" borderRadius={1}>
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                  <Typography variant="body2" fontWeight={500}>
                                    {attachment.filename}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {attachment.content_type} • {formatFileSize(attachment.byte_size)}
                                  </Typography>
                                </Box>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<DownloadIcon />}
                                  href={attachment.url}
                                  download={attachment.filename}
                                >
                                  Download
                                </Button>
                              </Box>
                              {isImage && (
                                <Box mt={1}>
                                  <img
                                    src={attachment.url}
                                    alt={attachment.filename}
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: '300px',
                                      objectFit: 'contain',
                                      borderRadius: '4px',
                                    }}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })()}

      <Card style={{ marginBottom: 16 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Message
          </Typography>
          <Divider style={{ marginBottom: 16 }} />
          {email.email_details?.body?.contentType === 'html' ? (
            <iframe
              srcDoc={email.email_details.body.content}
              sandbox=""
              title="Email message"
              style={{
                width: '100%',
                minHeight: 300,
                border: 'none',
              }}
              onLoad={(e) => {
                // Auto-resize iframe to fit content
                const iframe = e.target as HTMLIFrameElement;
                if (iframe.contentDocument?.body) {
                  iframe.style.height = iframe.contentDocument.body.scrollHeight + 'px';
                }
              }}
            />
          ) : (
            <Typography variant="body1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {email.email_details?.body?.content || email.body_preview || '(No content)'}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <ReceiptIcon color="action" />
            <Typography variant="h6" style={{ flexGrow: 1 }}>
              Vendor Invoice
            </Typography>
          </Box>
          {email.vendor_invoices && email.vendor_invoices.length > 0 ? (
            <Grid container spacing={2}>
              {email.vendor_invoices.map((invoice) => (
                <Grid item xs={12} key={invoice.id}>
                  <Box
                    p={2}
                    border={1}
                    borderColor="grey.300"
                    borderRadius={1}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {invoice.vendor_name || '(Vendor pending)'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {invoice.statement_number ? `${invoice.statement_number}` : 'Statement # pending'}
                        {invoice.line_items_total != null
                          ? ` · $${parseFloat(String(invoice.line_items_total)).toFixed(2)}`
                          : ''}
                        {invoice.pdf_filename ? ` · ${invoice.pdf_filename}` : ''}
                      </Typography>
                      <Chip label={invoice.validation_status || 'pending'} size="small" style={{ marginTop: 4 }} />
                    </Box>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={reparsing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                        onClick={handleReparseInvoice}
                        disabled={reparsing}
                      >
                        {reparsing ? 'Queuing...' : 'Re-extract Line Items'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ReceiptIcon />}
                        onClick={() => history.push(`/vendors/invoices/${invoice.id}`)}
                      >
                        View Invoice
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" color="textSecondary">
                No vendor invoice has been created from this email.
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={reimporting ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                onClick={handleReimportInvoice}
                disabled={reimporting || !email.has_attachments}
              >
                {reimporting ? 'Queuing...' : 'Regenerate Invoice'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Accordion defaultExpanded={false} sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Invoice Verification Flow</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <InvoiceWorkflowTracker emailId={id} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default OutlookEmailDetailPage;
